<template>
    <div 
        :id="props.lineId"
        class=" line no-outline"
        tabindex="0"
        @focus="onFocus"
    >
        <h5 v-if="props.title && props.items && props.items.length > 0" class="line__title">{{ props.title }}</h5>
        <div
            ref="root"
            class="line__content line__content_wrap"
            data-type="line-content"
        >
            <div 
                v-for="(item, index) in props.items"
                :key="calculateId(item)"
                class="line__card wrap"
                data-type="card"
            >
                <card 
                    :id="getId(index)"
                    :ref="setBaseCardRefs"
                    :card-data="item"
                    :data-rowId="props.lineId"
                    :data-keyup="getUpId(index)"
                    :data-keydown="getDownId(index)"
                    :data-keyleft="getPrevId(index)"
                    :data-keyright="getNextId(index)"
                    :index="index"
                    :component-name="item.getComponentName()"
                    @item-selected="onItemSelected"
                    @index-focused="onIndexFocused"
                    @focus="onFocusCard()"
                    @blur="onBlurCard()"
                />
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
    import { type PropType, ref, type Ref, computed, onBeforeUpdate, onBeforeMount, onBeforeUnmount, type ComponentPublicInstance } from "vue";
    import { type CardData } from "@/components/Cards/types";
    import { useApplicationStore } from "@/stores/Application";
    import type { IMultiLineViewExposed, IEventSelected } from "./types";

    const ApplicationStore = useApplicationStore();
    const { addSelectedIdProgram } = ApplicationStore;

    const props = defineProps({
        items: {
            type: (null as unknown) as PropType<CardData[] | null>,
            required: true
        },
        lineId: {
            type: String,
            required: true
        },
        title: {
            type: [String, null] as PropType<string | null>,
            required: false,
            default: null
        },
    });

    const emit = defineEmits(['list-item-selected', 'add-list-items', 'leave-up', 'ready']);
    const focusedIndex: Ref<number> = ref(0);
    const root: Ref<HTMLDivElement | null> = ref(null);
    const rowSize: Ref<number> = ref(0);
    const rowsCount = computed((): number => props.items ? Math.ceil(props.items.length / rowSize.value) : 0);
    const { $navigator } = useNuxtApp();
    const isRegKeyListeners = ref(false);

    let baseCardRefs: Array<Element | ComponentPublicInstance | null> = [];
    const setBaseCardRefs = (el: Element | ComponentPublicInstance | null) => {
        if (el) {
            baseCardRefs.push(el)
        }
    }

    const calculateId = (item: CardData): string => item.getId();

    const setItemToCenter = (index: number): void => {
        const selectedItem = baseCardRefs[index] as ComponentPublicInstance;
        if (selectedItem != null) {
            const element: HTMLElement | null = selectedItem.$el?.parentElement;

            if (element) {
                element.scrollIntoView({block: "center"});
            }
        }
    };

    const getId = (index: number): string | null => {
        if (props.items && props.items.length != 0 && index >= 0 && index < props.items.length) {
            return `${props.lineId}_${(props.items[index])?.getId()}`;
        } else {
            return null;
        }
    };
    
    const getUpId = (index: number): string | null => {
        const targetIdx = index - rowSize.value;
        return getId(targetIdx);
    };

    const getDownId = (index: number): string | null => {
        let targetIdx = index + rowSize.value;

        if(props.items && (targetIdx >= props.items.length)) {
            const currentRow = Math.floor(index / rowSize.value);
            const lastRow = rowsCount.value - 1; // Index of the last row

            if (currentRow !== lastRow) {
                // Calculate index for the last element in the last row if within bounds
                const lastRowStartIndex = lastRow * rowSize.value;
                if (index < lastRowStartIndex + (props.items.length % rowSize.value)) {
                    targetIdx = lastRowStartIndex + (props.items.length % rowSize.value) - 1;
                }
            }
        }
        return getId(targetIdx);
    };

    const getPrevId = (index: number): string | null => {
        return index == 0 ? null : getId(index - 1);
    };

    const getNextId = (index: number): string | null => {
        return props.items && (index < props.items.length - 1) ? getId(index + 1)  : null;
    };

    const onItemSelected = (item: CardData): void => {
        const idElement = `${props.lineId}_${item.getId()}`;

        addSelectedIdProgram(idElement);

        const selectedItem: IEventSelected = {
            item: item, 
            lineId: props.lineId, 
            componentName: item.getComponentName()
        };
        
        emit("list-item-selected", selectedItem);
    };

    const onIndexFocused = (index: number): void => {
        setItemToCenter(index);
        focusedIndex.value = index;

        if (rowSize.value === 0) {
            refreshColumnsNumber();
        }

        if (props.items && (index + 3 * rowSize.value >= props.items.length)) {
            emit("add-list-items");
        }
    };
    
    const setFocused = (id: string): void => {
        const element: HTMLElement | null = document.getElementById(id);
        if (element instanceof HTMLElement) {
            element.focus({preventScroll:true});
        }
    };

    const setFirstFocused = (): void => {
        const firstId = getId(0);
        if (props.items && props.items.length > 0 && firstId) {
            setFocused(firstId);
        }
    };

    const onFocus = (): void => {
        setFirstFocused();
    };

    const onFocusCard = () => {
        registerKeyListeners();
    };

    const onBlurCard = () => {
        unregisterKeyListeners();
    };

    const refreshColumnsNumber = (): void => {
        const cardElements = root.value?.querySelectorAll("[data-type='card']");
        const firstCardElement = cardElements?.[0] as HTMLElement;
        
        if (firstCardElement) {
            const computedStyle = getComputedStyle(firstCardElement);
            let firstCardElementWidth = firstCardElement.clientWidth;

            firstCardElementWidth +=
                parseFloat(computedStyle.marginLeft) +
                parseFloat(computedStyle.marginRight);
            const line = root.value as HTMLElement;
            const computedStyleLine = getComputedStyle(line);
            let elementWidth = line.clientWidth;

            elementWidth -=
                parseFloat(computedStyleLine.paddingLeft) +
                parseFloat(computedStyleLine.paddingRight);

            if (cardElements && cardElements.length > 0) {
                rowSize.value = Math.floor(
                    elementWidth / firstCardElementWidth
                );
            }
        }
    };
    const onKeyDown = (event: KeyboardEvent | HTMLElement | null): void => {
        if (event instanceof HTMLElement && !!event.dataset.keydown) {
            setFocused(event.dataset.keydown);
        }
    };
    const onKeyUp = (event: KeyboardEvent | HTMLElement | null): void => {
        if (event instanceof HTMLElement) {
            if (!event.dataset.keyup) {
                emit("leave-up");
                return;
            }
            setFocused(event.dataset.keyup);
        } else if (event instanceof KeyboardEvent && event.target instanceof HTMLElement) {
            if (!event.target.dataset.keyup) {
                emit("leave-up");
                return;
            }
        }
    };
    const registerKeyListeners = (): void => {
        if (!isRegKeyListeners.value) {
            $navigator.on("keypress:down", onKeyDown);
            $navigator.on("keypress:up", onKeyUp);
            isRegKeyListeners.value = true;
        }
    };
    const unregisterKeyListeners = (): void => {
        if (isRegKeyListeners.value) {
            $navigator.off("keypress:down", onKeyDown);
            $navigator.off("keypress:up", onKeyUp);
            isRegKeyListeners.value = false;
        }
    };
    const isEmpty = (): boolean => props.items !== null && props.items.length === 0;

    const setStateReady = (value: CardData[] | null): void => {
        if (Array.isArray(value)) {
            emit('ready');
        }
    };

    onMounted(() => {
        void nextTick(() => {
            setStateReady(props.items);
        });
    });

    onBeforeMount(() => {
        window.addEventListener("resize", refreshColumnsNumber);
    });
    onBeforeUnmount(() => {
        window.removeEventListener("resize", refreshColumnsNumber);
    })

    onBeforeUpdate(() => {
        baseCardRefs = []
    });

    watch(
        ()=> props.items, 
        (value): void => {
            setStateReady(value);
        }
    );

    defineExpose<IMultiLineViewExposed>({
        setFirstFocused,
        isEmpty
    });
</script>

<style lang="scss">
    @import url("styles.scss");
</style>